import React, { useEffect, useState } from 'react'
import { useDashboard, useUpcomingPrograms } from './api'
import IncubationListing from './Incubation/Listing'
import FacilitationList from './Facilitations/FacilitationList'
import ProgramList from './Programs/ProgramList'
import UpcomingProgramList from './UpcomingPrograms/UpcomingProgramList'
import Icons from '../../components/common/icons'
import { useAuthStore } from '../../store/authStore'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

const Dashboard: React.FC = () => {
  const { authenticated, userData } = useAuthStore()
  const { data, isLoading, isError } = useDashboard()
  const { data: upcomingData } = useUpcomingPrograms()
  const location = useLocation()
  const navigate = useNavigate()
  const departmentFilter = data?.department_filter
  const [selectedSection, setSelectedSection] = useState<
    'default' | 'incubation' | 'facilitation' | 'program' | 'upcoming'
  >('default')
  const [perPage, setPerPage] = useState(10)
  const [showProfilePrompt, setShowProfilePrompt] = useState(false)

  useEffect(() => {
    const path = location.pathname || ''
    if (path.endsWith('/incubation')) setSelectedSection('incubation')
    else if (path.endsWith('/programs')) setSelectedSection('program')
    else if (path.endsWith('/facilitations')) setSelectedSection('facilitation')
    else if (path.endsWith('/upcoming-programs')) setSelectedSection('upcoming')
    else if (path.endsWith('/dashboard')) setSelectedSection('default')
  }, [location.pathname])

  useEffect(() => {
    if (
      userData &&
      Object.prototype.hasOwnProperty.call(userData, 'is_profile_complete')
    ) {
      const isComplete =
        Number(userData.is_profile_complete) === 1 ||
        userData.is_profile_complete === true
      setShowProfilePrompt(!isComplete)
    }
  }, [userData, userData?.is_profile_complete])

  if (!authenticated) return <Navigate to="/login" replace />
  if (isLoading) return <div>Loading...</div>

  const stats = data?.data
    ? [
        {
          label: 'Registered Programs',
          value: data.data.registered_programs || 0,
        },
        {
          label: 'Upcoming Programs',
          value: upcomingData?.data?.length || 0,
        },
        // {
        //   label: 'Incubation Centre',
        //   value: data.data.registered_incubations || 0,
        // },
        {
          label: 'Facilitation Services',
          value: data.data.registered_facilitations || 0,
        },
      ]
    : []

  return (
    <div className="p-6 bg-[#f6f2f2] min-h-screen rounded-xl">
      {showProfilePrompt && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-50 flex items-center justify-center px-4">
          <div className="relative bg-white overflow-hidden rounded-3xl shadow-[0_25px_65px_rgba(16,24,40,0.2)] w-full max-w-lg p-10 border border-blue-100 text-center">
            <div className="absolute inset-0 opacity-40 pointer-events-none bg-gradient-to-br from-blue-200 via-sky-100 to-white" />
            <div className="relative">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.6}
                  stroke="currentColor"
                  className="w-8 h-8"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                Complete Your Profile
              </h3>
              <p className="text-gray-600 mb-7 max-w-md mx-auto">
                Keep your information up to date so we can personalize your
                dashboard experience.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  className="mx-auto px-8 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold py-2 rounded-xl shadow-md hover:shadow-lg transition"
                  onClick={() => {
                    setShowProfilePrompt(false)
                    navigate('/dashboard/profile-details')
                  }}
                >
                  Update Profile Now
                </button>
                <button
                  className="w-full text-sm text-gray-500 hover:text-gray-700 underline"
                  onClick={() => setShowProfilePrompt(false)}
                >
                  Remind me later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {isError ? (
        <p className="text-red-500">Error loading dashboard data.</p>
      ) : (
        <>
          {/* ===== Dashboard Summary Cards ===== */}
          {selectedSection === 'default' && (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {stats.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded shadow p-4 hover:bg-gray-50 cursor-pointer transition"
                  onClick={() => {
                    if (item.label === 'Incubation Centre')
                      setSelectedSection('incubation')
                    else if (item.label === 'Facilitation Services')
                      setSelectedSection('facilitation')
                    else if (item.label === 'Registered Programs')
                      setSelectedSection('program')
                    else if (item.label === 'Upcoming Programs')
                      setSelectedSection('upcoming')
                  }}
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="text-[34px] font-bold">{item.value}</div>
                    <Icons name="calendar-green" />
                  </div>
                  <div className="text-gray-500 text-[16px]">{item.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* ===== Listing Sections ===== */}
          <div className="bg-white rounded shadow p-4">
            {departmentFilter && (
              <div className="mb-4 text-sm text-gray-700">
                Filtering by Department: <strong>{departmentFilter}</strong>
              </div>
            )}

            {/* Default Table View */}
            {selectedSection === 'default' ? (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-200">
                    <span className="text-white text-lg">📅</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Upcoming Programs
                    </h2>
                    <p className="text-sm text-gray-500">
                      Discover and register for upcoming programs
                    </p>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-orange-200 via-amber-100 to-transparent my-4" />
                <UpcomingProgramList
                  perPage={perPage}
                  setPerPage={setPerPage}
                />
              </>
            ) : selectedSection === 'incubation' ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold">
                    My Incubation Application
                  </h2>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedSection('default')}
                      className="text-blue-600 underline text-sm"
                    >
                      ← Back to Dashboard
                    </button>
                  </div>
                </div>
                <IncubationListing perPage={perPage} setPerPage={setPerPage} />
              </>
            ) : selectedSection === 'program' ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200">
                      <span className="text-white text-lg">🎓</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        Registered Programs
                      </h2>
                      <p className="text-sm text-gray-500">
                        Your enrolled programs and bookings
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedSection('default')}
                      className="text-blue-600 underline text-sm"
                    >
                      ← Back to Dashboard
                    </button>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-blue-200 via-indigo-100 to-transparent mb-4" />
                <ProgramList perPage={perPage} setPerPage={setPerPage} />
              </>
            ) : selectedSection === 'upcoming' ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-200">
                      <span className="text-white text-lg">📅</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        Upcoming Programs
                      </h2>
                      <p className="text-sm text-gray-500">
                        Discover and register for upcoming programs
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedSection('default')}
                      className="text-blue-600 underline text-sm"
                    >
                      ← Back to Dashboard
                    </button>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-orange-200 via-amber-100 to-transparent mb-4" />
                <UpcomingProgramList
                  perPage={perPage}
                  setPerPage={setPerPage}
                />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-200">
                      <span className="text-white text-lg">🤝</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        Facilitation Services
                      </h2>
                      <p className="text-sm text-gray-500">
                        Your facilitation service requests
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedSection('default')}
                      className="text-blue-600 underline text-sm"
                    >
                      ← Back to Dashboard
                    </button>
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-emerald-200 via-teal-100 to-transparent mb-4" />
                <FacilitationList perPage={perPage} setPerPage={setPerPage} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default Dashboard
